import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Button, Stack, TextField } from '@mui/material';

import { Table, useTableState } from 'modules/ui/table';
import { Loader } from 'modules/ui/loader';
import { Label } from 'modules/ui/label';
import { useMessages } from 'modules/messages/hooks/useMessages';

import { getAllUsers, verifyUserEmail, resendVerificationEmail } from '../hooks/crud';
import { User } from '../interfaces';
import { LoadingButton } from '@mui/lab';

import VerifiedIcon from '@mui/icons-material/Verified';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';

export const UsersList = () => {
  const { t } = useTranslation('common');
  const { show } = useMessages();

  const [currentRowId, setCurrentRowId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [tableState, onChangeTableState] = useTableState({
    orderBy: [{ direction: 'asc', field: 'email' }],
  });

  const {
    data: users,
    isError,
    isLoading,
    error,
    refetch,
  } = useQuery<User[]>(['users'], getAllUsers);

  useEffect(() => {
    if (isError && error) {
      show({ message: `${t('general.messages.failed')}. ${error}` });
    }
  }, [isError, error, show, t]);

  const { mutate: verifyEmail, isLoading: isVerifying } = useMutation(verifyUserEmail, {
    onSuccess: () => {
      show({ message: t('general.messages.success'), severity: 'success' });
      refetch();
    },
    onError: (error: any) => {
      show({ message: `${t('general.messages.failed')}. ${error.message}` });
    },
    onSettled: () => setCurrentRowId(null),
  });

  const { mutate: resendCode, isLoading: isResending } = useMutation(resendVerificationEmail, {
    onSuccess: () => {
      show({ message: t('general.messages.success'), severity: 'success' });
    },
    onError: (error: any) => {
      show({ message: `${t('general.messages.failed')}. ${error.message}` });
    },
    onSettled: () => setCurrentRowId(null),
  });

  const handleVerify = useCallback(
    (userId: number) => () => {
      verifyEmail(userId);
    },
    [verifyEmail],
  );

  const handleResend = useCallback(
    (userId: number) => () => {
      resendCode(userId);
    },
    [resendCode],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'email',
        headerName: t('users.email'),
        flex: 1,
        minWidth: 250,
      },
      {
        field: 'full_name',
        headerName: t('users.fullName'),
        valueGetter: params => params.row.profile?.full_name || '-',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'phone_number',
        headerName: t('users.phoneNumber'),
        valueGetter: params => params.row.profile?.phone_number || '-',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'is_admin',
        headerName: t('users.isAdmin'),
        renderCell: (params: GridRenderCellParams) =>
          params.row.is_admin ? (
            <Label text={t('general.label.yes')} color="success" />
          ) : (
            <Label text={t('general.label.no')} color="default" />
          ),
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'email_verified',
        headerName: t('users.emailVerified'),
        renderCell: (params: GridRenderCellParams) =>
          params.row.email_verified ? (
            <Label text={t('general.label.yes')} color="success" />
          ) : (
            <Label text={t('general.label.no')} color="default" />
          ),
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'actions',
        headerName: t('users.actions'),
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams) => {
          const user = params.row;
          const isCurrentRow = currentRowId === user.id;

          if (!user.email) return null;
      
          return (
            <Stack gap={0.25} useFlexGap alignItems="flex-end" flex={1}>
              {!user.email_verified && (
                <LoadingButton
                  loading={isVerifying && isCurrentRow}
                  loadingPosition="start"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentRowId(user.id);
                    verifyEmail(user.id);
                  }}
                  variant="text"
                  startIcon={<VerifiedIcon />}
                >
                  {t('users.verifyEmail')}
                </LoadingButton>
              )}
              <LoadingButton
                loading={isResending && isCurrentRow}
                loadingPosition="start"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentRowId(user.id);
                  resendCode(user.id);
                }}
                variant="text"
                startIcon={<MarkEmailUnreadIcon />}
              >
                {t('users.resendVerification')}
              </LoadingButton>
            </Stack>
          );
        },
        flex: 1,
        minWidth: 250,
      },
    ],
    [t, handleVerify, handleResend],
  );

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users || [];
    return (users || []).filter((user) =>
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, users]);

  return (
    <>
      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          label={t('users.searchByEmail')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          variant="outlined"
        />
      </Stack>

      <Loader isLoading={isLoading || isVerifying || isResending} />

      
      <Table
        loading={isLoading || isVerifying || isResending}
        columns={columns}
        rows={filteredUsers || []}
        rowCount={filteredUsers ? filteredUsers.length : 0}
        state={tableState}
        onChangeState={onChangeTableState}
      />
    </>
  );
};
